import React, { forwardRef, useImperativeHandle, useRef } from 'react';

interface FilePickerProps {
  onFileSelected: (files: File[]) => void;
  accept?: string;
  style?: React.CSSProperties;
  multiple?: boolean;
}

export interface FilePickerRef {
  open: () => void;
}

/**
 * A component for selecting files from the file system
 *
 * Usage:
 *   const ref = useRef<FilePickerRef>(null);
 *   <FilePicker ref={ref} ... />
 *   ref.current?.open();
 */
export const FilePicker = forwardRef<FilePickerRef, FilePickerProps>(
  (props, ref) => {
    const { onFileSelected, ...rest } = props;
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      open: () => {
        inputRef.current?.click();
      }
    }));

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const fileArray = Array.from(files);
      onFileSelected(fileArray);

      event.target.value = '';
    };

    return (
      <input
        ref={inputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        {...rest}
      />
    );
  }
);
